// ============================================================
// Merkezi Regex Handler — Proje genelinde kullanılacak
// alan adına göre normalize / format / validate işlemleri
// ============================================================

// -------------------- REGEX TANIMLARI --------------------

export const REGEX = {
    // Sadece rakam
    DIGITS_ONLY: /[^0-9]/g,
    // Ondalıklı sayı (max 2 basamak)
    DECIMAL: /[^0-9.]/g,
    // Alfanümerik (büyük harf)
    ALPHA_NUMERIC_UPPER: /[^A-Za-z0-9]/g,
    // Türkçe harf + boşluk
    TR_LETTERS_SPACE: /[^a-zA-ZğüşıöçĞÜŞİÖÇ\s]/g,
    // Şase numarası – sadece alfanümerik, boşluk yok
    CHASSIS: /[^A-Za-z0-9]/g,
    // IBAN – büyük harf + rakam
    IBAN: /[^A-Z0-9]/g,
    // Plaka – alfanümerik
    LICENSE_PLATE: /[^A-Za-z0-9]/g,
    // Baştaki boşluklar
    LEADING_SPACES: /^\s+/,
    // Birden fazla ardışık boşluk
    MULTI_SPACE: /\s{2,}/g,
    // Harf ve rakam grupları (plaka formatlama)
    PLATE_GROUPS: /[A-Z]+|[0-9]+/g,

    // ─── DOĞRULAMA (invalid prop) ────────────────────────────
    // Büyük harf ad soyad — min 2 kelime (örn: "ALİ YILMAZ")
    FULL_NAME_UPPER_TR: /^[A-ZĞÜŞİÖÇ]+(?: [A-ZĞÜŞİÖÇ]+)+$/,
    // Title Case ad soyad — min 2 kelime (örn: "Ali Yılmaz")
    FULL_NAME_TITLE_TR: /^[A-ZĞÜŞİÖÇ][a-zğüşıöç]+(?: [A-ZĞÜŞİÖÇ][a-zğüşıöç]+)+$/,
    // Türkçe ad soyad — min 2 kelime, büyük/küçük karışık
    FULL_NAME_TR: /^[A-ZĞÜŞİÖÇa-zğüşıöç]+(\s[A-ZĞÜŞİÖÇa-zğüşıöç]+)+$/,
};

// -------------------- NORMALIZER FONKSİYONLARI --------------------

/** Sadece rakamlara izin ver, opsiyonel max uzunluk */
export const normalizeDigitsOnly = (value, maxLen) => {
    if (value === null || value === undefined) return '';
    let result = String(value).replace(REGEX.DIGITS_ONLY, '');
    if (maxLen && result.length > maxLen) result = result.slice(0, maxLen);
    return result;
};

/** Sayısal değeri Türkçe formatta göster — nokta→virgül */
export const displayDecimal = (value) => {
    if (value === null || value === undefined || value === '') return '';
    return String(value).replace('.', ',');
};

/** Ondalıklı sayı — max 2 basamak, tek nokta, baştaki sıfır temizlenir */
export const normalizeDecimal = (value) => {
    if (value === null || value === undefined) return '';
    let result = String(value).replace(/,/g, '.').replace(REGEX.DECIMAL, '');
    const parts = result.split('.');
    if (parts.length > 2) {
        result = parts[0] + '.' + parts.slice(1).join('');
    } else {
        if (parts[0].length > 1) parts[0] = parts[0].replace(/^0+/, '') || '0';
        result = parts.join('.');
    }
    if (parts.length === 2 && parts[1].length > 2) {
        result = parts[0] + '.' + parts[1].slice(0, 2);
    }
    return result;
};

/** Yüzde alanı — 0-100 arası tamsayı */
export const normalizePercent = (value) => {
    let result = normalizeDigitsOnly(value, 3);
    if (result && parseInt(result, 10) > 100) result = '100';
    return result;
};

/** Yüzde alanı — 0-100 arası, ondalıklı, nokta/virgül kabul eder, baştaki sıfır temizlenir */
export const normalizePercentDecimal = (value) => {
    if (value === null || value === undefined) return '';
    let result = String(value).replace(/,/g, '.').replace(REGEX.DECIMAL, '');
    const parts = result.split('.');
    if (parts.length > 2) {
        result = parts[0] + '.' + parts.slice(1).join('');
    } else {
        if (parts[0].length > 1) parts[0] = parts[0].replace(/^0+/, '') || '0';
        result = parts.join('.');
    }
    if (parts.length === 2 && parts[1].length > 2) {
        result = parts[0] + '.' + parts[1].slice(0, 2);
    }
    if (result && parseFloat(result) > 100) result = '100';
    return result;
};

/** Türkçe büyük harf */
export const normalizeUppercaseTR = (value) => {
    if (typeof value !== 'string') return value;
    return value.toLocaleUpperCase('tr-TR');
};

/** Türkçe küçük harf (email vb.) */
export const normalizeLowercaseTR = (value) => {
    if (typeof value !== 'string') return value;
    return value.trim().toLocaleLowerCase('tr-TR');
};

/** Title Case — Türkçe harf destekli */
export const normalizeTitleCaseTR = (value) => {
    if (typeof value !== 'string') return value;
    return value
        .replace(REGEX.TR_LETTERS_SPACE, '')
        .replace(REGEX.LEADING_SPACES, '')
        .replace(REGEX.MULTI_SPACE, ' ')
        .split(' ')
        .map(word => {
            if (!word) return '';
            return word.charAt(0).toLocaleUpperCase('tr-TR') + word.slice(1).toLocaleLowerCase('tr-TR');
        })
        .join(' ');
};

/** Ad soyad: Büyük harf Türkçe + boşluk, ek boşluk temizleme */
export const normalizeFullNameUpperTR = (value) => {
    if (typeof value !== 'string') return value;
    const cleaned = value
        .replace(REGEX.TR_LETTERS_SPACE, '')
        .replace(REGEX.MULTI_SPACE, ' ');
    return normalizeUppercaseTR(cleaned);
};

/**
 * Plaka formatlama (strict) — Türk plaka formatı: XX YYY ZZZZ
 * Grup 1: 2 rakam (il kodu) | Grup 2: 1-3 harf (seri) | Grup 3: 2-4 rakam (sıra no)
 * Her grup arasına otomatik boşluk eklenir; sıra dışı karakterler reddedilir.
 */
export const normalizeLicensePlate = (value) => {
    if (typeof value !== 'string') return value || '';
    const cleaned = value.replace(REGEX.LICENSE_PLATE, '').toLocaleUpperCase('tr-TR');
    if (!cleaned) return '';

    let digitsPart1 = '';
    let lettersPart = '';
    let digitsPart2 = '';
    let i = 0;

    // Faz 1: İlk 2 rakam (il kodu)
    while (i < cleaned.length && digitsPart1.length < 2) {
        if (/[0-9]/.test(cleaned[i])) digitsPart1 += cleaned[i];
        i++;
    }

    // Faz 2: 1-3 harf (seri)
    while (i < cleaned.length && lettersPart.length < 3) {
        if (/[A-Z]/.test(cleaned[i])) {
            lettersPart += cleaned[i];
        } else if (/[0-9]/.test(cleaned[i]) && lettersPart.length > 0) {
            break; // Harfler bitti, rakam fazına geç
        }
        i++;
    }

    // Faz 3: 2-4 rakam (sıra no)
    while (i < cleaned.length && digitsPart2.length < 4) {
        if (/[0-9]/.test(cleaned[i])) digitsPart2 += cleaned[i];
        i++;
    }

    let result = digitsPart1;
    if (lettersPart) result += ' ' + lettersPart;
    if (digitsPart2) result += ' ' + digitsPart2;
    return result;
};

/**
 * Plaka formatlama (search/filter) — Gevşek normalizasyon.
 * Harf ve rakam gruplarını boşlukla ayırır, sıra kısıtlaması yoktur.
 * Arama/filtre kutularında kullanılır.
 */
export const normalizeLicensePlateSearch = (value) => {
    if (typeof value !== 'string') return value || '';
    const cleaned = value.replace(REGEX.LICENSE_PLATE, '').toLocaleUpperCase('tr-TR');
    if (!cleaned) return '';
    const parts = cleaned.match(REGEX.PLATE_GROUPS);
    if (!parts) return cleaned;
    return parts.join(' ');
};

/** Şase numarası — alfanümerik, büyük harf, max 17 */
export const normalizeChassisNumber = (value) => {
    if (typeof value !== 'string') return value || '';
    return value.replace(REGEX.CHASSIS, '').toLocaleUpperCase('tr-TR').slice(0, 17);
};

/** IBAN normalize — büyük harf alfanümerik */
export const normalizeIban = (value) => {
    if (typeof value !== 'string') return value || '';
    return value.toLocaleUpperCase('tr-TR').replace(/\s/g, '').replace(REGEX.IBAN, '');
};

/** Telefon numarası — sadece rakam, max 11, 05 ile başlamalı */
export const normalizePhone = (value) => {
    if (value === null || value === undefined) return '';
    let result = String(value).replace(REGEX.DIGITS_ONLY, '').slice(0, 11);
    // İlk karakterler 05 ile başlamalı — kullanıcıyı engelleme, sadece normalize et
    return result;
};

/** Sigorta poliçe numarası — alfanümerik büyük harf */
export const normalizeInsuranceNumber = (value) => {
    if (typeof value !== 'string') return value || '';
    return value.replace(REGEX.ALPHA_NUMERIC_UPPER, '').toLocaleUpperCase('tr-TR');
};

/** SWIFT/BIC kodu — büyük harf alfanümerik, max 11 karakter */
export const normalizeSwiftBic = (value) => {
    if (typeof value !== 'string') return value || '';
    return value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 11);
};


/** Para girişi (string) — sadece rakam, nokta, virgül, baştaki sıfır temizlenir */
export const normalizeCurrencyInput = (value) => {
    if (value === null || value === undefined) return '';
    let result = String(value).replace(/[^0-9.,]/g, '');
    const dotIndex = result.indexOf('.');
    const commaIndex = result.indexOf(',');
    const sepIndex = dotIndex >= 0 ? dotIndex : commaIndex;
    if (sepIndex > 1) {
        result = result.slice(0, sepIndex).replace(/^0+/, '') + result.slice(sepIndex);
        if (result.startsWith('.') || result.startsWith(',')) result = '0' + result;
    } else if (sepIndex < 0 && result.length > 1) {
        result = result.replace(/^0+/, '') || '0';
    }
    return result;
};

/** Araç yılı — 4 hane, 1900-günümüz */
export const normalizeCarYear = (value) => {
    let result = normalizeDigitsOnly(value, 4);
    // 4 hane dolduysa aralık kontrolü
    if (result.length === 4) {
        const year = parseInt(result, 10);
        const currentYear = new Date().getFullYear();
        if (year < 1900) result = '1900';
        if (year > currentYear) result = String(currentYear);
    }
    return result;
};

/** MERSIS numarası — sadece rakam, max 16 */
export const normalizeMersisNumber = (value) => {
    return normalizeDigitsOnly(value, 16);
};

/** Tarih dönüştürme — string → Date veya null */
export const normalizeDate = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
};

// ─── BACKWARD-COMPATIBLE ALIAS'LAR ──────────────────────────
// validators.js'den taşınan eski isimler — mevcut tüketici dosyalar bozulmasın
export const normalizeDigits = normalizeDigitsOnly;
export const normalizeEmail = normalizeLowercaseTR;


// ====================================================================
// YARDIMCI DÖNÜŞÜM FONKSİYONLARI
// ====================================================================

/** Türkçe özel karakterleri İngilizce karşılıklarına çevirir */
const TURKISH_CHAR_MAP = {
    'Ç': 'C', 'ç': 'c', 'Ğ': 'G', 'ğ': 'g', 'İ': 'I', 'ı': 'i',
    'Ö': 'O', 'ö': 'o', 'Ş': 'S', 'ş': 's', 'Ü': 'U', 'ü': 'u'
};
const TURKISH_CHAR_REGEX = /[ÇçĞğİıÖöŞşÜü]/g;

export const turkishToEnglish = (text) => {
    if (typeof text !== 'string') return text || '';
    return text.replace(TURKISH_CHAR_REGEX, (match) => TURKISH_CHAR_MAP[match] || match);
};

/** Email normalize — Türkçe karakter dönüşümü + küçük harf + trim */
export const normalizeEmailInput = (value) => {
    if (typeof value !== 'string') return value;
    return turkishToEnglish(value).trim().toLowerCase();
};

/** Para/tutar string → float dönüşümü (virgülü noktaya çevirir) */
export const parseCurrencyToFloat = (value, fallback = 0) => {
    if (value === null || value === undefined || value === '') return fallback;
    const normalized = String(value).replace(/,/g, '.');
    const result = parseFloat(normalized);
    return isNaN(result) ? fallback : result;
};


// ====================================================================
// VALIDATION SABİTLERİ VE FONKSİYONLARI (validators.js'den taşındı)
// ====================================================================

export const IBAN_LENGTHS = {
    AL: 28, AD: 24, AT: 20, AZ: 28, BH: 22, BE: 16, BA: 20, BR: 29, BG: 22,
    CR: 22, HR: 21, CY: 28, CZ: 24, DK: 18, DO: 28, EE: 20, FO: 18, FI: 18,
    FR: 27, GE: 22, DE: 22, GI: 23, GR: 27, GL: 18, GT: 28, HU: 28, IS: 26,
    IE: 22, IL: 23, IT: 27, JO: 30, KZ: 20, KW: 30, LV: 21, LB: 28, LI: 21,
    LT: 20, LU: 20, MT: 31, MR: 27, MU: 30, MD: 24, MC: 27, ME: 22, NL: 18,
    MK: 19, NO: 15, PK: 24, PS: 29, PL: 28, PT: 25, QA: 29, RO: 24, SM: 27,
    SA: 24, RS: 22, SK: 24, SI: 19, ES: 24, SE: 24, CH: 21, TN: 24, TR: 26,
    AE: 23, GB: 22, VG: 24,
};

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const PHONE_TR_REGEX = /^05\d{9}$/;
export const TCID_REGEX = /^\d{11}$/;
export const TAX_TR_REGEX = /^\d{10}$/;
export const IBAN_REGEX = /^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/;
export const SWIFT_BIC_REGEX = /^[A-Z]{6}[A-Z0-9]{2}(?:[A-Z0-9]{3})?$/;
export const ORDER_NUMBER_REGEX = /^-?[0-9]+$/;
export const POSITIVE_INTEGER_REGEX = /^\d+$/;
export const DATE_YYYY_MM_DD_REGEX = /^\d{4}-\d{2}-\d{2}$/;
export const LICENSE_PLATE_TR_REGEX = /^\d{2}[A-Z]{1,3}\d{2,4}$/;
export const TITLE_TR_REGEX = /^[A-ZĞÜŞİÖÇa-zğüşıöç ]{3,50}$/i;
export const NAME_TR_REGEX = /^[A-ZĞÜŞİÖÇa-zğüşıöç\s]+$/;
export const CHASSIS_VIN_REGEX = /^[A-HJ-NPR-Z0-9]{17}$/;
export const WEBSITE_REGEX = /^(https?:\/\/)?([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;

/** Pozitif tam sayı doğrulaması (opsiyonel boş) */
export const validatePositiveInteger = (value) => {
    if (value === null || value === undefined || value === '') return true;
    return POSITIVE_INTEGER_REGEX.test(String(value));
};

/** YYYY-MM-DD tarih string doğrulaması */
export const validateDateYyyyMmDd = (value) => {
    if (!value) return false;
    return DATE_YYYY_MM_DD_REGEX.test(String(value));
};

/** Türk plaka doğrulama — 2 rakam + 1-3 harf + 2-4 rakam */
export const validateLicensePlate = (value) => {
    if (!value) return false;
    const normalized = normalizeLicensePlate(String(value)).replace(/\s/g, '');
    return LICENSE_PLATE_TR_REGEX.test(normalized);
};

/** IBAN doğrulama — format + ülke uzunluk kontrolü */
export const validateIban = (value) => {
    const normalized = normalizeIban(value);
    if (!normalized) return { isValid: true };

    if (!IBAN_REGEX.test(normalized)) {
        return { isValid: false, error: 'Geçerli bir IBAN formatı giriniz.' };
    }

    const countryCode = normalized.slice(0, 2);
    const expectedLength = IBAN_LENGTHS[countryCode];
    if (expectedLength && normalized.length !== expectedLength) {
        return { isValid: false, error: `IBAN uzunluğu ${expectedLength} karakter olmalıdır (${countryCode}).` };
    }

    return { isValid: true };
};

/** E-posta doğrulama */
export const validateEmail = (value) => {
    if (!value) return true;
    return EMAIL_REGEX.test(String(value).trim());
};

/** TC Kimlik No doğrulama — 11 hane */
export const validateTcid = (value) => {
    const normalized = normalizeDigitsOnly(value, 11);
    return TCID_REGEX.test(normalized);
};

/** Vergi No doğrulama — 10 hane */
export const validateTaxNumber = (value) => {
    const normalized = normalizeDigitsOnly(value, 10);
    return TAX_TR_REGEX.test(normalized);
};

/** Türk telefon numarası doğrulama — 05XXXXXXXXX */
export const validatePhoneTr = (value) => {
    const normalized = normalizeDigitsOnly(value, 11);
    return PHONE_TR_REGEX.test(normalized);
};


// ====================================================================
// API / SEARCH UTILITIES (normalizers.js'den taşındı)
// ====================================================================

export const normalizeSearchValue = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim();
};

export const appendQueryParamIfMinLength = (params, key, value, minLength) => {
    const normalized = normalizeSearchValue(value);
    if (normalized.length >= minLength) {
        params.append(key, normalized);
    }
    return normalized;
};

export const normalizeSearchFields = (fields = []) => {
    const normalized = {};
    let hasInvalid = false;

    fields.forEach(({ key, value, minLength }) => {
        const normalizedValue = normalizeSearchValue(value);
        normalized[key] = normalizedValue;

        if (minLength && normalizedValue.length > 0 && normalizedValue.length < minLength) {
            hasInvalid = true;
        }
    });

    return { normalized, hasInvalid };
};

export const normalizeApiItems = (resp) => {
    if (resp?.data?.items && Array.isArray(resp.data.items)) return resp.data.items;
    if (Array.isArray(resp?.items)) return resp.items;
    if (Array.isArray(resp?.data)) return resp.data;
    if (Array.isArray(resp)) return resp;
    return [];
};

export const normalizeSelectOptions = (resp, labelKeys = ['title', 'name']) => {
    const items = normalizeApiItems(resp);
    return items
        .map((item) => {
            const label = labelKeys.map((key) => item?.[key]).find(Boolean) || '';
            return { value: item?.id, label };
        })
        .filter((opt) => opt.value && opt.label);
};


// ====================================================================
// ANA FIELD CONFIG — alan adına göre normalizer eşleştirmesi
// ====================================================================

/**
 * Her alan adı için uygulanacak normalizer fonksiyonu.
 * `normalizer`: (value) => normalizedValue
 * `description`: Regex/kural açıklaması (belgeleme amaçlı)
 */
export const FIELD_CONFIG = {

    // ─── PLAKA / ARAÇ ────────────────────────────────────────
    licensePlate:                   { normalizer: normalizeLicensePlate,       description: 'Plaka (strict): XX YYY ZZZZ formatı' },
    plate:                          { normalizer: normalizeLicensePlate,       description: 'Plaka (alias): XX YYY ZZZZ formatı' },
    licensePlateNumber:             { normalizer: normalizeLicensePlate,       description: 'Plaka numarası: XX YYY ZZZZ formatı' },
    counterpartyLicensePlateNumber: { normalizer: normalizeLicensePlate,       description: 'Karşı taraf plaka: XX YYY ZZZZ formatı' },
    plateSearch:                    { normalizer: normalizeLicensePlateSearch, description: 'Plaka arama: gevşek gruplandırma, sıra kısıtı yok' },
    caseNumberSearch:               { normalizer: (v) => turkishToEnglish(normalizeUppercaseTR(v || '')), description: 'Dosya no arama: büyük harf + Türkçe->İng dönüşüm' },
    enforcementCaseSearch:          { normalizer: (v) => turkishToEnglish(normalizeUppercaseTR(v || '')), description: 'İcra dosya no arama: büyük harf + Türkçe->İng dönüşüm' },
    arbitrationCaseSearch:          { normalizer: (v) => turkishToEnglish(normalizeUppercaseTR(v || '')), description: 'Tahkim dosya no arama: büyük harf + Türkçe->İng dönüşüm' },
    chassisNumber:                  { normalizer: normalizeChassisNumber,      description: 'Şase: alfanümerik, büyük harf, max 17 karakter' },
    carchassisNumber:               { normalizer: normalizeChassisNumber,      description: 'Araç şase no (alias): alfanümerik, büyük harf, max 17 karakter' },
    carBrand:                       { normalizer: normalizeUppercaseTR,        description: 'Araç markası: büyük harf' },
    carModel:                       { normalizer: normalizeUppercaseTR,        description: 'Araç modeli: büyük harf' },
    carYear:                        { normalizer: normalizeCarYear,            description: 'Araç yılı: 4 hane, 1900-günümüz' },
    carOwner:                       { normalizer: normalizeUppercaseTR,        description: 'Araç sahibi: büyük harf' },

    // ─── KİMLİK / VERGİ ─────────────────────────────────────
    tcid:                           { normalizer: (v) => normalizeDigitsOnly(v, 11),   description: 'TC Kimlik: 11 hane rakam' },
    tcId:                           { normalizer: (v) => normalizeDigitsOnly(v, 11),   description: 'TC Kimlik (alias): 11 hane rakam' },
    identityNumber:                 { normalizer: (v) => normalizeDigitsOnly(v, 11),   description: 'TC Kimlik (alias): 11 hane rakam' },
    counterpartyIdentificationNumber: { normalizer: (v) => normalizeDigitsOnly(v, 11), description: 'Karşı taraf TC: 11 hane rakam' },
    taxNumber:                      { normalizer: (v) => normalizeDigitsOnly(v, 10),   description: 'Vergi No: 10 hane rakam' },
    counterpartyTaxNumber:          { normalizer: (v) => normalizeDigitsOnly(v, 10),   description: 'Karşı taraf Vergi No: 10 hane rakam' },
    crsNumber:                      { normalizer: normalizeMersisNumber,    description: 'MERSIS: sadece rakam, max 16 hane' },
    tcIdSearch:                     { normalizer: (v) => normalizeDigitsOnly(v, 11), description: 'TC/Vergi arama: sadece rakam, max 11' },

    // ─── İSİMLER ─────────────────────────────────────────────
    name:                           { normalizer: normalizeUppercaseTR,     description: 'Ad/isim: büyük harf' },
    clientName:                     { normalizer: normalizeFullNameUpperTR,  description: 'Müvekkil adı: Türkçe büyük harf, sadece harf+boşluk' },
    fullName:                       { normalizer: normalizeFullNameUpperTR,  description: 'Ad Soyad: Türkçe büyük harf, sadece harf+boşluk' },
    counterpartyCarOwner:           { normalizer: normalizeTitleCaseTR,      description: 'Karşı taraf araç sahibi: Title Case Türkçe' },
    middleman:                      { normalizer: normalizeTitleCaseTR,      description: 'Aracı: Title Case Türkçe' },
    companyRepresentative:          { normalizer: normalizeUppercaseTR,      description: 'Firma yetkilisi: büyük harf' },
    contactPerson:                  { normalizer: normalizeUppercaseTR,      description: 'İletişim kişisi: büyük harf' },

    // ─── İLETİŞİM ───────────────────────────────────────────
    email:                          { normalizer: normalizeEmailInput,     description: 'Email: Türkçe→İng karakter dönüşümü, küçük harf, trim' },
    email2:                         { normalizer: normalizeEmailInput,     description: 'Email 2: Türkçe→İng karakter dönüşümü, küçük harf, trim' },
    phoneNumber:                    { normalizer: normalizePhone,           description: 'Telefon: 11 hane, 05 ile başlar' },
    phoneNumber2:                   { normalizer: normalizePhone,           description: 'Telefon 2: 11 hane, 05 ile başlar' },

    // ─── IBAN ────────────────────────────────────────────────
    iban:                           { normalizer: normalizeIban,            description: 'IBAN: büyük harf alfanümerik' },
    ibanNumber:                     { normalizer: normalizeIban,            description: 'IBAN numarası' },
    iban2:                          { normalizer: normalizeIban,            description: 'IBAN 2' },
    middlemanIban:                  { normalizer: normalizeIban,            description: 'Aracı IBAN' },
    cpIban:                         { normalizer: normalizeIban,            description: 'Karşı taraf IBAN' },

    // ─── YÜZDE / ORAN ───────────────────────────────────────
    customerContractRate:                       { normalizer: normalizePercentDecimal,  description: 'Sözleşme oranı: %0-100, ondalıklı' },
    contractRate:                               { normalizer: normalizePercentDecimal,  description: 'Sözleşme oranı: %0-100, ondalıklı' },
    customerEnforcementAttorneyFeeShareRate:    { normalizer: normalizePercentDecimal,  description: 'İcra vekalet hakediş oranı: %0-100, ondalıklı' },
    enforcementAttorneyFeeShareRate:            { normalizer: normalizePercentDecimal,  description: 'İcra vekalet hakediş oranı: %0-100, ondalıklı' },
    customerJudgmentAttorneyFeeShareRate:       { normalizer: normalizePercentDecimal,  description: 'İlam vekalet hakediş oranı: %0-100, ondalıklı' },
    judgmentAttorneyFeeShareRate:               { normalizer: normalizePercentDecimal,  description: 'İlam vekalet hakediş oranı: %0-100, ondalıklı' },
    ourFaultRate:                               { normalizer: normalizePercentDecimal,  description: 'Kusur oranı: %0-100, ondalıklı' },
    faultStatus:                                { normalizer: normalizePercentDecimal,  description: 'Kusur oranı (alias): %0-100, ondalıklı' },
    customerMiddlemanContractRate:              { normalizer: normalizePercentDecimal,  description: 'Aracı sözleşme oranı: %0-100, ondalıklı' },
    middlemanContractRate:                      { normalizer: normalizePercentDecimal,  description: 'Aracı sözleşme oranı: %0-100, ondalıklı' },

    // ─── PARA TUTARLARI (tamsayı) ────────────────────────────
    notaryFeeAmount:                { normalizer: normalizeDecimal,         description: 'Noter ücreti: max 2 ondalık' },
    estimatedDepreciationAmount:    { normalizer: normalizeDecimal,         description: 'Tahmini değer kaybı: max 2 ondalık' },
    damageAmount:                   { normalizer: normalizeDecimal,         description: 'Hasar tutarı: max 2 ondalık' },
    damageDifferenceAmount:         { normalizer: normalizeDecimal,         description: 'Hasar farkı: max 2 ondalık' },
    lorAmount:                      { normalizer: normalizeDecimal,         description: 'Hak mahrumiyeti tutarı: max 2 ondalık' },
    customerMiddlemanContractPrice: { normalizer: normalizeDecimal,         description: 'Aracı sözleşme fiyatı: max 2 ondalık' },
    middlemanContractPrice:         { normalizer: normalizeDecimal,         description: 'Aracı sözleşme fiyatı: max 2 ondalık' },

    // ─── PARA TUTARLARI (ondalıklı) ─────────────────────────
    amount:                         { normalizer: normalizeCurrencyInput,   description: 'Tutar girişi: rakam + nokta/virgül' },
    updateAmount:                   { normalizer: normalizeCurrencyInput,   description: 'Tutar güncelleme girişi: rakam + nokta/virgül' },
    arbitrationApplicationFee:      { normalizer: normalizeDecimal,         description: 'Tahkim başvuru ücreti: max 2 ondalık' },
    bonusEligibility:               { normalizer: normalizeDecimal,         description: 'Prim hakediş: max 2 ondalık' },
    acceptedAmount:                 { normalizer: normalizeCurrencyInput,   description: 'Kabul edilen tutar: rakam + nokta/virgül' },
    enforcementAttorneyFee:         { normalizer: normalizeCurrencyInput,   description: 'İcra vekalet ücreti: rakam + nokta/virgül' },
    judgmentAttorneyFee:            { normalizer: normalizeCurrencyInput,   description: 'Hükmedilen vekalet ücreti: rakam + nokta/virgül' },
    additionalExpenseAmount:        { normalizer: normalizeCurrencyInput,   description: 'Ek gider tutarı: rakam + nokta/virgül' },
    principalClaim:                 { normalizer: normalizeDecimal,         description: 'Asıl alacak: max 2 ondalık' },
    interestAmount:                 { normalizer: normalizeDecimal,         description: 'Faiz tutarı: max 2 ondalık' },
    attorneyFee:                    { normalizer: normalizeDecimal,         description: 'Vekalet ücreti: max 2 ondalık' },
    litigationFee:                  { normalizer: normalizeDecimal,         description: 'Yargılama gideri: max 2 ondalık' },
    acceptedAppraisalFee:           { normalizer: normalizeDecimal,         description: 'Kabul edilen ekspertiz ücreti: max 2 ondalık' },
    powerOfAttorneyFee:             { normalizer: normalizeDecimal,         description: 'Vekalet ücreti: max 2 ondalık' },
    objectionFee:                   { normalizer: normalizeDecimal,         description: 'İtiraz ücreti: max 2 ondalık' },
    opposingPowerOfAttorneyFee:     { normalizer: normalizeDecimal,         description: 'Karşı vekalet ücreti: max 2 ondalık' },
    expertWitnessPrice:             { normalizer: normalizeDecimal,         description: 'Bilirkişi ücreti: max 2 ondalık' },
    ewClaimDeterminationFee:        { normalizer: normalizeDecimal,         description: 'Talep tespit tutarı: max 2 ondalık' },
    amendmentFee:                   { normalizer: normalizeDecimal,         description: 'Islah ücreti: max 2 ondalık' },
    lovClaimAmount:                 { normalizer: normalizeDecimal,         description: 'Önceki LOV tutarı: max 2 ondalık' },
    lpImpeachmentAmount:            { normalizer: normalizeDecimal,         description: 'Plaka azil tutarı: max 2 ondalık' },
    priceForNow:                    { normalizer: normalizeDecimal,         description: 'Güncel tutar: max 2 ondalık' },
    arbitrationPriceForNow:         { normalizer: normalizeDecimal,         description: 'Şimdilik tutar: max 2 ondalık' },
    value:                          { normalizer: normalizeDecimal,         description: 'Sabit değişken değeri: max 2 ondalık' },

    // ─── SİGORTA NUMARALARI ─────────────────────────────────
    trafficInsuranceNumber:         { normalizer: normalizeInsuranceNumber, description: 'Trafik sigorta no: alfanümerik büyük harf' },
    cascoInsuranceNumber:           { normalizer: normalizeInsuranceNumber, description: 'Kasko no: alfanümerik büyük harf' },
    counterpartyTrafficInsuranceNumber: { normalizer: normalizeInsuranceNumber, description: 'Karşı taraf trafik sigorta no' },
    counterpartyCascoInsuranceNumber:   { normalizer: normalizeInsuranceNumber, description: 'Karşı taraf kasko no' },

    // ─── BÜYÜK HARF METİN ALANLARI ──────────────────────────
    companyName:                    { normalizer: normalizeUppercaseTR,     description: 'Şirket adı: büyük harf' },
    reference:                      { normalizer: normalizeUppercaseTR,     description: 'Referans: büyük harf' },
    address:                        { normalizer: normalizeUppercaseTR,     description: 'Adres: büyük harf' },
    counterpartyAddress:            { normalizer: normalizeUppercaseTR,     description: 'Karşı taraf adres: büyük harf' },
    powerOfAttorneyNumber:          { normalizer: normalizeUppercaseTR,     description: 'Vekalet no: büyük harf' },
    notaryNumber:                   { normalizer: normalizeUppercaseTR,     description: 'Noter: büyük harf' },
    recipientOfPowerOfAttorney:     { normalizer: normalizeUppercaseTR,     description: 'Vekaleti alan: büyük harf' },
    phoneDescription:               { normalizer: normalizeUppercaseTR,     description: 'Telefon açıklaması: büyük harf' },
    phoneDescription2:              { normalizer: normalizeUppercaseTR,     description: 'Telefon 2 açıklaması: büyük harf' },
    emailDescription:               { normalizer: normalizeUppercaseTR,     description: 'Email açıklaması: büyük harf' },
    emailDescription2:              { normalizer: normalizeUppercaseTR,     description: 'Email 2 açıklaması: büyük harf' },
    ibanDescription:                { normalizer: normalizeUppercaseTR,     description: 'IBAN açıklaması: büyük harf' },
    ibanDescription2:               { normalizer: normalizeUppercaseTR,     description: 'IBAN 2 açıklaması: büyük harf' },

    // ─── GENEL METİN ALANLARI ───────────────────────────────
    description:                    { normalizer: normalizeUppercaseTR,     description: 'Açıklama: büyük harf' },
    title:                          { normalizer: normalizeUppercaseTR,     description: 'Başlık: büyük harf' },
    text:                           { normalizer: normalizeUppercaseTR,     description: 'Metin: büyük harf' },
    expenseType:                    { normalizer: normalizeUppercaseTR,     description: 'Masraf tipi: büyük harf' },
    paymentCollectionType:          { normalizer: normalizeUppercaseTR,     description: 'Tahsilat tipi: büyük harf' },
    paymentMethod:                  { normalizer: normalizeUppercaseTR,     description: 'Ödeme yöntemi: büyük harf' },
    invoiceNumber:                  { normalizer: normalizeDigitsOnly,      description: 'Fatura no: sadece rakam' },
    receiptNumber:                  { normalizer: normalizeDigitsOnly,      description: 'Makbuz no: sadece rakam' },
    year:                           { normalizer: (v) => normalizeDigitsOnly(v, 4), description: 'Yıl: 4 hane rakam' },
    ewDefectDeterminationFee:       { normalizer: normalizePercentDecimal,  description: 'Kusur tespit: %0-100, ondalıklı' },

    // ─── SWIFT/BIC ───────────────────────────────────────────
    swiftBicCode:                   { normalizer: normalizeSwiftBic,        description: 'SWIFT/BIC: büyük harf alfanümerik, max 11' },

    // ─── PERSONEL / KİŞİ ALANLARI ───────────────────────────
    firstName:                      { normalizer: normalizeUppercaseTR,     description: 'Ad: büyük harf' },
    lastName:                       { normalizer: normalizeUppercaseTR,     description: 'Soyad: büyük harf' },
    job:                            { normalizer: normalizeUppercaseTR,     description: 'Görev: büyük harf' },
    department:                     { normalizer: normalizeUppercaseTR,     description: 'Departman: büyük harf' },
    notaryOfPoa:                    { normalizer: normalizeUppercaseTR,     description: 'Vekaletname noteri: büyük harf' },
    payerName:                      { normalizer: normalizeUppercaseTR,     description: 'Ödeme yapan: büyük harf' },

    // ─── BANKA ALANLARI ───────────────────────────────────
    bankName:                       { normalizer: normalizeUppercaseTR,     description: 'Banka adı: büyük harf' },
    bankCode:                       { normalizer: normalizeUppercaseTR,     description: 'Banka kodu: büyük harf' },
    branchName:                     { normalizer: normalizeUppercaseTR,     description: 'Şube adı: büyük harf' },
    branchCode:                     { normalizer: normalizeUppercaseTR,     description: 'Şube kodu: büyük harf' },
    comment:                        { normalizer: normalizeUppercaseTR,     description: 'Yorum: büyük harf' },
};


// ====================================================================
// ANA HANDLER FONKSİYONU
// ====================================================================

/**
 * Alan adına göre değeri normalize eder.
 * FIELD_CONFIG'de tanımlı bir normalizer varsa onu uygular,
 * yoksa değeri olduğu gibi döndürür.
 *
 * @param {string} fieldName — alan adı (Input name attribute)
 * @param {*} value — ham girdi değeri
 * @returns {*} — normalize edilmiş değer
 */
export const normalizeFieldValue = (fieldName, value) => {
    const config = FIELD_CONFIG[fieldName];
    if (!config || !config.normalizer) return value;
    return config.normalizer(value);
};

/**
 * Bir alanın FIELD_CONFIG'de tanımlı olup olmadığını kontrol eder.
 * @param {string} fieldName
 * @returns {boolean}
 */
export const hasFieldConfig = (fieldName) => {
    return fieldName in FIELD_CONFIG;
};

/**
 * Alan açıklamasını döndürür (tooltip / belgeleme amaçlı).
 * @param {string} fieldName
 * @returns {string|null}
 */
export const getFieldDescription = (fieldName) => {
    const config = FIELD_CONFIG[fieldName];
    return config ? config.description : null;
};
